import { differenceInDays } from 'date-fns';
import normalize from 'json-api-normalizer';

import { getProcessFrom } from '../../domain';
import { formatDateTime, formatDateToUTC } from '../../shared/utilities/date';
import { DisplayProject, DossierOverviewsJSONAPIResponse } from '../dossier-overview/types';
import { inTripReviewSerializer } from '../in-trip-review/serializers';
import { preTripReviewSerializer } from '../pre-trip-review/serializers';
import { IncludedTag } from '../tags/api/api';

import {
    AvailableTasksFromAPI,
    ContractDeferAgreementFromAPI,
    ContractVouchersFromAPI,
    CustomerCareCrisisTicket,
    CustomerCareCrisisTicketFromAPI,
    DeferAgreement,
    DossierFromAPI,
    DossierId,
    DossierJSONAPIResponse,
    DossierOverview,
    DossierTag,
    Memo,
    MemoFromAPI,
    NormalizedAvailableTasks,
    NormalizedInitialRequest,
    NormalizedParticipants,
    Project,
    RelationshipValue,
    SerializedDossier,
    SerializedDossiers,
    SerializedTripProject,
    TravellerInformation,
    TravellerInformationFromAPI,
    TripProjectField,
    TripProjectJSONAPIResponse,
    Voucher,
} from './types';

export function projectSerializer({
    arrivalDate,
    approximateArrivalDate,
    approximateDurationInDays,
    departureDate,
    ...restOfProject
}: Project): DisplayProject {
    const arrivalDateInUTC = formatDateToUTC(arrivalDate);
    const departureDateInUTC = formatDateToUTC(departureDate);
    const approximateArrivalDateInUTC = formatDateToUTC(approximateArrivalDate);

    const duration =
        departureDateInUTC && arrivalDateInUTC
            ? differenceInDays(departureDateInUTC, arrivalDateInUTC) + 1
            : approximateDurationInDays;

    const expectedArrivalDate = arrivalDateInUTC ? arrivalDateInUTC : approximateArrivalDateInUTC;

    return {
        ...restOfProject,
        approximateDurationInDays,
        approximateArrivalDate: approximateArrivalDateInUTC,
        arrivalDate: arrivalDateInUTC,
        duration,
        expectedArrivalDate,
        departureDate: departureDateInUTC,
    };
}

// @ts-ignore TODO : Refactor see https://github.com/Evaneos/ets-request-manager-app/issues/1106
const taskSerializer = (task) => ({
    dueDate: formatDateToUTC(task.dueDate),
    closeDate: formatDateToUTC(task.closeDate),
    definition: task.definition,
    actionType: task.actionType,
    note: task.note,
});

// @ts-ignore TODO : Refactor see https://github.com/Evaneos/ets-request-manager-app/issues/1106
function dossierSerializer(dossier, tags: DossierTag[]): DossierOverview {
    return {
        id: dossier.id,
        ...dossier.attributes,
        ...dossier.meta,
        agentId: dossier.attributes.agentId.toString(),
        archiveDate: formatDateTime(dossier.attributes.archiveDate),
        discussion: {
            ...dossier.attributes.discussion,
            lastAgentToTravellerMessageDate: formatDateTime(
                dossier.attributes.discussion.lastAgentToTravellerMessageDate,
            ),
            lastTravellerToAgentMessageDate: formatDateTime(
                dossier.attributes.discussion.lastTravellerToAgentMessageDate,
            ),
            lastMessageReadDate: formatDateTime(dossier.attributes.discussion.lastMessageReadDate),
        },
        process: getProcessFrom(dossier.attributes.step),
        project: projectSerializer(dossier.attributes.project),
        task: taskSerializer(dossier.attributes.task),
        testimonialReceptionDate: formatDateToUTC(dossier.attributes.testimonialReceptionDate),
        requestDate: formatDateTime(dossier.attributes.requestDate),
        tags,
    };
}

export const memoSerializer = (memo: MemoFromAPI): Memo => memo.attributes.memo;

// @ts-ignore TODO : Refactor see https://github.com/Evaneos/ets-request-manager-app/issues/1106
const initialRequestSerializer = (initialRequest): NormalizedInitialRequest =>
    initialRequest.attributes;

// @ts-ignore TODO : Refactor see https://github.com/Evaneos/ets-request-manager-app/issues/1106
const participantsSerializer = (participants): NormalizedParticipants => participants.attributes;

const availableTasksSerializer = (
    // @ts-ignore TODO : Refactor see https://github.com/Evaneos/ets-request-manager-app/issues/1106
    availableTasks,
): NormalizedAvailableTasks => ({
    ...availableTasks.attributes,
    suggested: availableTasks.attributes.suggested.task_definition,
});

export const customerCareCrisisTicketSerializer = (
    customerCareCrisisTicket: CustomerCareCrisisTicketFromAPI,
): CustomerCareCrisisTicket => customerCareCrisisTicket.attributes;

export const contractVouchersSerializer = (contractVouchers: ContractVouchersFromAPI): Voucher[] =>
    contractVouchers.attributes.vouchers;

export const contractDeferAgreementSerializer = (
    contractDeferAgreement: ContractDeferAgreementFromAPI,
): DeferAgreement => contractDeferAgreement.attributes.contractDeferAgreement;

export const travellerInformationSerializer = (
    requesterId: number,
    travellerInformation: TravellerInformationFromAPI,
): TravellerInformation => ({
    ...travellerInformation.attributes,
    travellerId: requesterId,
    trips: travellerInformation.attributes.trips.map((trip) => ({
        ...trip,
        id: trip.id.toString(),
    })),
    openedRequests: travellerInformation.attributes.openedRequests.map((openedRequest) => ({
        id: openedRequest.id.toString(),
        destination: openedRequest.destination,
        requestDate: formatDateToUTC(openedRequest.requestDate),
        arrivalDate: formatDateToUTC(openedRequest.arrivalDate),
        step: openedRequest.step,
    })),
});

export const dossierTagsMapper = (
    tags: Record<string, IncludedTag> | undefined,
    dossierTagsRelationships: RelationshipValue[] | RelationshipValue,
): DossierTag[] => {
    if (!tags || !Array.isArray(dossierTagsRelationships)) {
        return [];
    }

    return dossierTagsRelationships.map((tag) => ({
        color: `#${tags[tag.id].attributes.color}`,
        name: tags[tag.id].attributes.name,
        id: tags[tag.id].id,
    }));
};

export function serializeDossierFromAPI(json: DossierJSONAPIResponse): SerializedDossier {
    const dossierId = json.data.id;
    const {
        //@ts-ignore
        customerCareCrisisTicket,
        //@ts-ignore
        dossierOverview,
        //@ts-ignore
        dossierMemo,
        //@ts-ignore
        dossierInitialRequest,
        //@ts-ignore
        dossierParticipants,
        //@ts-ignore
        contractVouchers,
        //@ts-ignore
        contractDeferAgreement,
        //@ts-ignore
        preTripReview,
        //@ts-ignore
        inTripReview,
        //@ts-ignore
        travellerInformation,
        //@ts-ignore,
        tag,
    } = normalize(json);
    // This avoid to camelize object's keys which are step names and used as translations keys
    const availableTasks = normalize<{
        'available-tasks': { [key in DossierId]: AvailableTasksFromAPI };
    }>(json, { camelizeKeys: false })['available-tasks'];
    const { requesterId } = dossierOverview[dossierId].attributes.project;

    const mappedTags = dossierTagsMapper(
        tag,
        dossierOverview?.[dossierId]?.relationships?.tags?.data ?? [],
    );

    return {
        id: dossierId,
        dossier: {
            [dossierId]: dossierSerializer(dossierOverview[dossierId], mappedTags),
        },
        ...(dossierMemo && {
            dossierMemo: {
                [dossierId]: memoSerializer(dossierMemo[dossierId]),
            },
        }),
        ...(dossierInitialRequest && {
            dossierInitialRequest: {
                [dossierId]: initialRequestSerializer(dossierInitialRequest[dossierId]),
            },
        }),
        ...(dossierParticipants && {
            dossierParticipants: {
                [dossierId]: participantsSerializer(dossierParticipants[dossierId]),
            },
        }),
        ...(availableTasks && {
            dossierAvailableTasks: {
                [dossierId]: availableTasksSerializer(availableTasks[dossierId]),
            },
        }),
        ...(customerCareCrisisTicket && {
            dossierCustomerCareCrisisTicket: {
                [dossierId]: customerCareCrisisTicketSerializer(
                    customerCareCrisisTicket[dossierId],
                ),
            },
        }),
        ...(contractVouchers && {
            contractVouchers: {
                [dossierId]: contractVouchersSerializer(contractVouchers[dossierId]),
            },
        }),
        ...(contractDeferAgreement && {
            contractDeferAgreement: {
                [dossierId]: contractDeferAgreementSerializer(contractDeferAgreement[dossierId]),
            },
        }),
        ...(preTripReview && {
            preTripReview: {
                [dossierId]: preTripReviewSerializer(preTripReview[dossierId]),
            },
        }),
        ...(inTripReview && {
            inTripReview: {
                [dossierId]: inTripReviewSerializer(inTripReview[dossierId]),
            },
        }),
        ...(travellerInformation && {
            travellerInformation: {
                [dossierId]: travellerInformationSerializer(
                    requesterId,
                    travellerInformation[requesterId],
                ),
            },
        }),
    };
}

export function serializeDossierOverviewFromAPI(
    json: DossierOverviewsJSONAPIResponse,
): SerializedDossiers {
    const { dossierOverview: dossiers, tag } = normalize<{
        dossierOverview: (DossierOverview & Pick<DossierFromAPI, 'relationships'>)[];
        tag: Record<string, IncludedTag> | undefined;
    }>(json);

    return {
        dossier: dossiers
            ? Object.fromEntries(
                  Object.entries(dossiers).map(
                      ([key, dossier]: [
                          string,
                          DossierOverview & Pick<DossierFromAPI, 'relationships'>,
                      ]) => {
                          if (!tag) {
                              return [key, dossierSerializer(dossier, [])];
                          }
                          const mappedTags = dossierTagsMapper(
                              tag,
                              dossier.relationships.tags?.data ?? [],
                          );
                          return [key, dossierSerializer(dossier, mappedTags)];
                      },
                  ),
              )
            : {},
    };
}

/**
 * Serializes trip project JSON API response into a HashMap for easy field access.
 *
 * Transforms array response into an object where field keys map to field data:
 * - `data.hosting` -> TripProjectFieldMultipleChoice (array values)
 * - `data.projectStep` -> TripProjectFieldSingle (string value)
 *
 * Validates that value types match fieldType to ensure runtime type safety.
 * Invalid fields are logged and skipped rather than throwing to prevent UI crashes.
 *
 * @param json - JSON API response from /dossier/:id/trip-project endpoint
 * @returns HashMap of field key to field data, with only valid recognized fields included
 */
export function tripProjectSerializer(json: TripProjectJSONAPIResponse): SerializedTripProject {
    if (!json.data || !Array.isArray(json.data)) {
        return {};
    }

    const result: Partial<Record<string, TripProjectField>> = {};

    for (const field of json.data) {
        const { key, keyTranslated, value, valueTranslated, fieldType } = field.attributes;

        // Validate value type matches fieldType for runtime type safety
        if (fieldType === 'multipleChoice') {
            if (!Array.isArray(value) || !Array.isArray(valueTranslated)) {
                console.error(`Invalid multipleChoice field ${key}: expected arrays`, {
                    value,
                    valueTranslated,
                });
                continue; // Skip invalid field
            }
        } else {
            if (typeof value !== 'string' || typeof valueTranslated !== 'string') {
                console.error(`Invalid ${fieldType} field ${key}: expected strings`, {
                    value,
                    valueTranslated,
                });
                continue; // Skip invalid field
            }
        }

        result[field.id] = {
            key,
            keyTranslated,
            value,
            valueTranslated,
            fieldType,
        } as TripProjectField;
    }

    return result as SerializedTripProject;
}
